import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IDeviceService } from '@intigral/domain/lib/application/services/DeviceService';
import { Spinner } from '@intigral/shared';
import { getDeviceFingerprint } from '@intigral/utils';
import { dataStreamTypes } from '@libs/constants';
import url from '../../src/helpers/url';
import user from '../../src/helpers/user.js';
import { ProfileSettings } from '../components/ui/ProfileSettings';
import { UserDevices } from '../components/ui/UserDevices';

type UserProfileScreenProps = {
    screenName: string;
    screenKey: string;
};

export const UserProfileScreen: React.FC<UserProfileScreenProps> = ({ screenName, screenKey }) => {
    const [currentFingerprint, setCurrentFingerprint] = useState<string | null>(null);
    const [currentDeviceGuid, setCurrentDeviceGuid] = useState<string | null>(null);

    const username = user.getUsername();

    useEffect(() => {
        const fingerprint = getDeviceFingerprint(username);
        setCurrentFingerprint(fingerprint);
    }, [username]);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['user-profile', username],
        queryFn: async () => {
            const userInfo = user.getUserInfo();
            const _devices = await container
                .resolve<IDeviceService>(DI.SERVICES.IDeviceService)
                .getDevices(userInfo.username, userInfo.apiKey, userInfo.authToken, userInfo.identityToken);
            const profileList = await user.initiatedUserProfiles(true);
            return { devices: _devices, profiles: profileList };
        },
    });

    useEffect(() => {
        if (data?.devices && currentFingerprint) {
            const currentDevice = data.devices.find((device) => device.deviceUniqueIdentifier === currentFingerprint);

            if (currentDevice) {
                setCurrentDeviceGuid(currentDevice.deviceGuid);
            }
        }
    }, [data, currentFingerprint]);

    const meta = {
        carousel_link_target: 'movies',
        defaultActive: 'carousel-view',
        cta: {
            is_enabled: 'yes',
            title: {
                ar: 'إقران جهاز التلفزيون',
                en: 'Pair TV Device',
            },
            url: {
                ar: process.env.NX_PUBLIC_SUBSCRIPTION_BASE_URL + 'pair',
                en: process.env.NX_PUBLIC_SUBSCRIPTION_BASE_URL + 'en/pair',
            },
        },
        data_stream_type: 'user_devices',
        empty_msg: 'false',
        hasMultiview: 'false',
        is_rented: false,
        listbtn_target: 'movies',
        programType: 'all',
        seeAllScreen: 'false',
        subscriptionGuid: user.getUserSubscribedPackages(),
        subscriptionGuids: 'true',
    };

    if (isLoading) {
        return <Spinner opaqueBg />;
    }

    if (isError) {
        return 'profile load error';
    }

    return (
        <>
            <ProfileSettings profileList={data.profiles} />
            <UserDevices
                id="profile-settings_UserDevices_571"
                data_stream={dataStreamTypes.USER_DEVICES}
                meta={meta}
                content={data.devices}
                maxdeviceReached={url.getQuery('maxDeviceReached')}
                currentFingerprint={currentFingerprint}
                currentDeviceGuid={currentDeviceGuid}
            />
        </>
    );
};
